import './styles.scss';

import { Col, InputGroup, Row } from 'react-bootstrap';
import { Dayts, createNotification } from '@/utils';
import { EnumDropdownField, getItemsFromEnum } from '@/components/EnumDropdown';
import { PotentialTripKindEnum, PotentialTripTypeNameEnum } from '@/models/gen/graphql';
import { useMemo, useState } from 'react';

import { AirlineIataDropdownField } from '@/components/AirlineDropdown';
import { AirportDropdownField } from '@/components/AirportDropdown';
import CalendarField from './components/CalendarField';
import { ClientDropdownField } from '@/components/ClientDropdown';
import { FlightNumberOrLoopNameInputField } from '@/common/FlightNumberInput';
import Form from '@/common/Form';
import { InputField } from '@/components/Input';
import { KindsDropdownField } from '@/components/KindsDropdown';
import { LoadingBlur } from '@/components/LoadingSpinner';
import { LocationQueryDropdownField } from '@/components/LocationDropdown';
import { NumberInputField } from '@/components/NumberInput';
import { PotentialTripFormDefinitions } from './types';
import CommonTimeInput from '@/common/TimeInput';
import { fieldWrapper } from '@/components/Field';
import { Toast } from '@/models';
import { createComponentState } from '@/state';
import createPotentialTripBulk from '@/api/services/imports/createPotentialTripBulk';
import { useClientIdsThatRequireCrew } from '@/api/services/providers/searchProviders';
import useLocale from '@/hooks/useLocale';

export interface PotentialTripsFormState {
  data: Partial<PotentialTripFormDefinitions.Value>;
}
export const usePotentialTripsForm = createComponentState<PotentialTripsFormState>({
  data: {},
});

// Helper function to auto-select airport location based on trip kind using pre-loaded map
const TimeInputField = fieldWrapper(CommonTimeInput, 'TimeInputField');

const autoSelectAirportLocation = (
  airportCode: string,
  kind: PotentialTripKindEnum,
  form: any,
  airportLocationMap: Map<string, string>
): void => {
  if (!airportCode || !kind) return;

  const locationId = airportLocationMap.get(airportCode.toUpperCase());
  if (!locationId) return;

  const key = kind === PotentialTripKindEnum.Arr ? 'puLocation' : 'doLocation';

  form.setValue(key, locationId);
  form.trigger(key);
};

const PotentialTripsForm = ({ onSubmit, locale: customLocale, onClose, onRefresh }: PotentialTripFormDefinitions.Props) => {
  const [{ data: formData }, setState] = usePotentialTripsForm(({ state, setState }) => [state, setState]);
  const [loading, setLoading] = useState(false);
  const locale = useLocale({
    ...customLocale,
  });
  const [{ data: providersThatRequireCrew, loading: loadingCrewProviders }] = useClientIdsThatRequireCrew();

  // Use localStorage hooks to access cached data
  const airportLocationMap = useMemo(() => {
    const airportsData = JSON.parse(window.localStorage.getItem('airportCode') || '{"items": []}');
    const locationsData = JSON.parse(window.localStorage.getItem('puLocationId') || '{"items": []}');
    // Airport structure: { label: "SAN", value: "SAN", keywords: ["SAN"] }
    // Skip airports with group arrays
    const airports = airportsData?.items || [];

    // Location structure: { label: "TRU by hilton Euless WEST", keywords: ["TRU", "by", ...], value }
    const locations = locationsData?.items || [];

    // Build a map of airport code -> location ID
    const map = new Map<string, string>();

    // For each airport, match airport code with location keywords
    airports.forEach((airport) => {
      // Skip airports with groups
      if (airport.group) {
        return;
      }

      const airportCode = airport.value || airport.label;

      if (!airportCode) return;

      // Find location where label includes this airport code
      const matchingLocation = locations.find((loc) => loc.label?.toUpperCase() === airportCode.toUpperCase());

      map.set(airportCode, matchingLocation?.value || undefined);
    });

    return map;
  }, []);

  const handleSubmit = async (data) => {
    try {
      setLoading(true);

      if (!data || !data.scheduledDate) {
        console.error('Form data or scheduledDate is missing', data);
        createNotification('Invalid form data', Toast.Type.DANGER, 'Potential Trip');
        return;
      }

      // Transform flightNumberOrLoopName into flightNumber or loopName
      const flightNumberOrLoopName = data.flightNumberOrLoopName?.trim() || '';
      const isFlightNumber = /^\d{4}$/.test(flightNumberOrLoopName);

      const scheduledDates = Array.isArray(data.scheduledDate) ? data.scheduledDate : [data.scheduledDate];

      const payloads = scheduledDates.map((date) => {
        const scheduled = new Dayts(date).setTime(data.scheduledTime || '00:00:00').formatAs(Dayts.Format.DATETIME_RFC);

        return {
          typeName: data.typeName,
          payerProviderId: data.payerProviderId,
          airportCode: data.airportCode,
          servicerIataAirlineCode: data.servicerIataAirlineCode,
          scheduled: scheduled,
          kind: data.kind,
          flightNumber: isFlightNumber ? parseInt(flightNumberOrLoopName, 10) : undefined,
          loopName: !isFlightNumber ? flightNumberOrLoopName : undefined,
          pilots: data.pilots || 0,
          attendants: data.attendants || 0,
          puLocationId: data.puLocation,
          doLocationId: data.doLocation,
          split: data.split || 0,
          fromManifest: Number(data?.fromManifest || 0),
          bufferMinutes: 0,
          importId: data.importId,
          // Optional UPS fields
          ...(data.crewId && { crewId: data.crewId }),
          ...(data.firstName && { firstName: data.firstName }),
          ...(data.lastName && { lastName: data.lastName }),
        };
      });

      const res = await createPotentialTripBulk(...payloads);

      const successCount = res?.output?.length || 0;
      createNotification(
        `Successfully Created ${successCount} Potential Trip${successCount > 1 ? 's' : ''}`,
        Toast.Type.SUCCESS,
        'Potential Trip'
      );

      // Update zustand state with the last created trip
      const lastTrip = res?.output?.[res.output.length - 1]?.node;
      if (lastTrip) {
        setState((current) => ({
          ...current,
          data: { ...current.data, ...lastTrip },
        }));
      }

      onSubmit?.(data);
    } catch (err) {
      console.error('Error Creating Potential Trip.', err);
      createNotification('Could Not Create Potential Trip', Toast.Type.DANGER, 'Potential Trip');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsCopy = async (values) => {
    await handleSubmit(values);
    onRefresh?.();
  };

  const handleCancel = () => {
    PotentialTripFormDefinitions.clearForm();
    setState((current) => ({
      ...current,
      data: {},
    }));
    onClose?.();
  };

  const handleFormSubmit = async (data) => {
    await handleSubmit(data);
    PotentialTripFormDefinitions.clearForm();
    onClose?.();
    onRefresh?.();
  };
  const fullValue = useMemo(
    () => ({
      ...PotentialTripFormDefinitions.initValue,
      ...formData,
      ...PotentialTripFormDefinitions.loadForm(),
    }),
    [formData]
  );
  return (
    <Form
      name="PotentialTripsForm"
      className="PotentialTripsForm"
      onSubmit={handleFormSubmit}
      value={fullValue}
      validator={PotentialTripFormDefinitions.validator(locale)}
      onChange={PotentialTripFormDefinitions.saveForm}
    >
      <LoadingBlur loading={loading || loadingCrewProviders} />
      <Form.Input name="id" type="hidden" />
      <Form.Input name="tripId" type="hidden" />
      <Form.Input name="importId" type="hidden" />
      <Form.Input name="bufferMinutes" type="hidden" />
      <Row>
        <Col xs={6} className="d-flex flex-column">
          <Form.Input
            as={EnumDropdownField}
            name="typeName"
            label={locale('Sch Type')}
            enum={getItemsFromEnum(PotentialTripTypeNameEnum)}
            validate
          />
          <Form.Input as={ClientDropdownField} name="payerProviderId" label={locale('Client')} validate />
          <Form.Input as={AirlineIataDropdownField} name="servicerIataAirlineCode" label={locale('Airline')} validate />
          <Form.Watch name="airportCode">
            {(_, form) => (
              <>
                <Form.Input
                  as={AirportDropdownField}
                  name="airportCode"
                  label={locale('Airport')}
                  onChange={(value) => {
                    form.setValue('dropCity', value || '');

                    const kind = form.getValues('kind');
                    if (kind) {
                      const locationId = airportLocationMap.get(value.toUpperCase());
                      if (locationId) {
                        const key = kind === PotentialTripKindEnum.Arr ? 'puLocation' : 'doLocation';
                        form.setValue(key, locationId || '');
                      }
                    }

                    form.trigger(['dropCity', 'puLocation', 'doLocation']);
                  }}
                  validate
                />
                <Form.Input
                  as={TimeInputField}
                  name="scheduledTime"
                  label={locale('Sch Time')}
                  onChange={(value: string) => {
                    value = (value || '').replace(/[^0-9:]/g, '');
                    if (!value?.includes(':') && value?.length > 0) {
                      const input = value.padStart(4, '0');
                      const hours = input.slice(0, 2);
                      const minutes = input.slice(2, 4);
                      return `${hours}:${minutes}:00`;
                    }
                    form.setValue('scheduledTime', value);
                    form.trigger('scheduledTime');
                    return value;
                  }}
                  validate
                />
              </>
            )}
          </Form.Watch>
        </Col>
        <Col xs={6}>
          <Form.Input as={CalendarField} name="scheduledDate" label={locale('Pickup Date')} validate />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Watch name="kind">
            {(_, form) => (
              <Form.Input
                as={KindsDropdownField}
                name="kind"
                enum={PotentialTripKindEnum}
                label={locale('Arrival / Departure')}
                onChange={(value) => {
                  const [puLocation, doLocation, airportCode, dropCity] = form.getValues([
                    'puLocation',
                    'doLocation',
                    'airportCode',
                    'dropCity',
                  ]);

                  const bothEmpty = (!puLocation || puLocation === '') && (!doLocation || doLocation === '');

                  // If both empty, auto-select
                  if (bothEmpty && airportCode && value) {
                    const locationId = airportLocationMap.get(airportCode.toUpperCase());
                    if (locationId) {
                      const key = value === PotentialTripKindEnum.Arr ? 'puLocation' : 'doLocation';
                      form.setValue(key, locationId || '');
                      form.trigger([key, 'airportCode', 'dropCity']);
                    }
                    return;
                  }

                  // Swap locations and airports
                  form.setValue('puLocation', doLocation || '');
                  form.setValue('doLocation', puLocation || '');
                  form.setValue('airportCode', dropCity || '');
                  form.setValue('dropCity', airportCode || '');
                  form.trigger(['puLocation', 'doLocation', 'airportCode', 'dropCity']);
                }}
                validate
              />
            )}
          </Form.Watch>
        </Col>
        <Col xs={6}>
          <Form.Input as={FlightNumberOrLoopNameInputField} name="flightNumberOrLoopName" label={locale('Flight # / Loop Name')} validate />
        </Col>
      </Row>

      <Form.Watch name={['pilots', 'attendants']}>
        {(_values: [number, number], form) => {
          return (
            <Row>
              <Col xs={6}>
                <Form.Input
                  as={NumberInputField}
                  onChange={(value) => {
                    form.setValue('pilots', value || 0);
                    form.trigger(['pilots', 'attendants']);
                  }}
                  name="pilots"
                  label={locale('Plt')}
                  min={0}
                  validate
                />
              </Col>
              <Col xs={6}>
                <Form.Input
                  as={NumberInputField}
                  onChange={(value) => {
                    form.setValue('attendants', value || 0);
                    form.trigger(['pilots', 'attendants']);
                  }}
                  name="attendants"
                  label={locale('F/A')}
                  min={0}
                  validate
                />
              </Col>
            </Row>
          );
        }}
      </Form.Watch>
      <Form.Watch name="payerProviderId">
        {([payerProviderId]: [string]) =>
          providersThatRequireCrew?.includes(payerProviderId || '') && (
            <>
              <Row>
                <Col xs={12}>
                  <Form.Input as={NumberInputField} name="crewId" label={locale('Crew Id')} min={0} validate />
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <Form.Input as={InputField} name="firstName" label={locale('First Name')} validate />
                </Col>
                <Col xs={6}>
                  <Form.Input as={InputField} name="lastName" label={locale('Last Name')} validate />
                </Col>
              </Row>
            </>
          )
        }
      </Form.Watch>
      <Form.Watch name={['airportCode', 'dropCity', 'puLocation', 'doLocation']}>
        {([airportCode, dropCity]: [string, string, string, string]) => {
          return (
            <>
              <Row className="PULocation py-2">
                <Col xs={12} className="d-flex gap-1">
                  <div className="d-flex align-items-center">
                    <InputGroup.Text>{locale('P/U')}</InputGroup.Text>
                  </div>
                  <div className="w-100 LocationDropdown">
                    <Form.Input
                      as={LocationQueryDropdownField}
                      options={{ autoSelectLocation: true, showAirportPrefix: false, locale: { 'Select...': locale('Select Location') } }}
                      airports={airportCode}
                      onCreate={() => {}}
                      filterActiveLocations
                      name="puLocation"
                      validate
                    />
                  </div>
                </Col>
              </Row>
              <Row className="DOLocation py-2">
                <Col xs={12} className="d-flex gap-1">
                  <div className="d-flex align-items-center">
                    <InputGroup.Text>{locale('D/O')}</InputGroup.Text>
                  </div>
                  <div className="w-100 LocationDropdown">
                    <Form.Input
                      as={LocationQueryDropdownField}
                      options={{ autoSelectLocation: true, showAirportPrefix: false, locale: { 'Select...': locale('Select Location') } }}
                      airports={dropCity}
                      onCreate={() => {}}
                      filterActiveLocations
                      name="doLocation"
                      validate
                    />
                  </div>
                  <Form.Input className="DOCity" as={AirportDropdownField} options={{ showClearButton: false }} name="dropCity" />
                </Col>
              </Row>
            </>
          );
        }}
      </Form.Watch>
      <Row>
        <Col xs={6}>
          <InputGroup className="FromManifestSwitch">
            <label>{locale('Include This Trip In Future Manifests?')}</label>
            <div>
              <Form.Switch checkedChildren={locale('Yes')} unCheckedChildren={locale('No')} name="fromManifest" />
            </div>
          </InputGroup>
        </Col>
        <Col xs={6}>
          <Form.Input as={NumberInputField} name="split" label={locale('Split')} min={0} />
        </Col>
      </Row>

      <div className="d-flex flex-column gap-2 mt-4">
        <Row>
          <Col xs={12}>
            <Form.Watch>
              {(_, form) => {
                const isValid = form.formState.isValid;
                return (
                  <Form.Button
                    className="w-100"
                    type="button"
                    variant="dark"
                    onClick={handleSaveAsCopy}
                    disabled={!isValid || loadingCrewProviders}
                  >
                    {locale('Save As Copy')}
                  </Form.Button>
                );
              }}
            </Form.Watch>
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            <Form.Button className="w-100" type="button" variant="secondary" onClick={handleCancel}>
              {locale('Cancel')}
            </Form.Button>
          </Col>
          <Col xs={6}>
            <Form.Button className="w-100" type="submit" variant="primary" disabled={loadingCrewProviders}>
              {locale('Save')}
            </Form.Button>
          </Col>
        </Row>
      </div>
    </Form>
  );
};

export default PotentialTripsForm;
